/**
 * GlobalAiActivity — indicador GLOBAL de operação de IA em andamento.
 *
 * Usa useIsMutating() do React Query: sempre que QUALQUER mutation está rodando
 * (os botões de IA — sugerir escopo, blueprint, recomendações, squad, agentes,
 * componentes, análise…), mostra uma barra fina no topo + um cronômetro.
 *
 * Cobre TODOS os botões de uma vez, sem instrumentar cada um. Filtra por duração
 * (só aparece após ~600ms) para NÃO piscar em ações rápidas (save/toggle).
 */
import { useIsMutating } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { C } from '@/theme/palette';

export function GlobalAiActivity() {
  const active = useIsMutating();
  const [show, setShow] = useState(false);
  const [sec, setSec] = useState(0);

  // Aparece só se a operação durar > 600ms (evita flash em mutations rápidas).
  useEffect(() => {
    let delayT;
    if (active > 0) {
      delayT = setTimeout(() => setShow(true), 600);
    } else {
      setShow(false);
      setSec(0);
    }
    return () => clearTimeout(delayT);
  }, [active]);

  // Cronômetro enquanto visível.
  useEffect(() => {
    if (!show) return undefined;
    const t = setInterval(() => setSec((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [show]);

  if (!show) return null;

  const mm = String(Math.floor(sec / 60)).padStart(2, '0');
  const ss = String(sec % 60).padStart(2, '0');
  const cyber = C.cyber ?? '#00E0E0';

  return (
    <div
      role="status"
      aria-live="polite"
      style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999, pointerEvents: 'none' }}
    >
      {/* Barra indeterminada no topo */}
      <div style={{ height: 3, width: '100%', overflow: 'hidden' }}>
        <div
          style={{
            height: '100%',
            width: '40%',
            background: `linear-gradient(90deg, transparent, ${cyber}, transparent)`,
            animation: 'navaGlobalBar 1.1s ease-in-out infinite',
          }}
        />
      </div>
      {/* Pill central com spinner + cronômetro */}
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 6 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '5px 12px',
            borderRadius: 999,
            fontSize: 12,
            fontWeight: 600,
            background: C.blackElev ?? '#141414',
            color: C.neutral ?? '#e8e8e8',
            border: `1px solid ${cyber}55`,
            boxShadow: '0 4px 14px rgba(0,0,0,.35)',
          }}
        >
          <span
            aria-hidden="true"
            style={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              border: `2px solid ${cyber}`,
              borderTopColor: 'transparent',
              display: 'inline-block',
              animation: 'navaGlobalSpin 0.8s linear infinite',
            }}
          />
          Processando com IA…
          <span style={{ fontFamily: 'monospace', color: C.neutralDim ?? '#8a8a8a' }}>
            {mm}:{ss}
          </span>
        </div>
      </div>
      <style>{`
        @keyframes navaGlobalBar{0%{transform:translateX(-120%)}100%{transform:translateX(360%)}}
        @keyframes navaGlobalSpin{to{transform:rotate(360deg)}}
      `}</style>
    </div>
  );
}
